import { Button, ButtonGroup, Divider } from '@blueprintjs/core'
import * as React from 'react'
import { useBlockLayout, useResizeColumns, useTable, Column as ReactTableColumn, Row } from 'react-table'
import { UprightStyle } from '../../types'

const styles: UprightStyle = {
  container: { display: 'flex', justifyContent: 'center', paddingBottom: 20, marginTop: 20 },
  info: { margin: `10px 20px 10px 20px`, fontWeight: 600 },
  button: { width: 102, borderRadius: 3 },
  tableScroller: { width: '100%', overflowX: 'auto' },
  table: { width: 'max-content', minWidth: '100%' },
  row: { width: 'max-content', minWidth: '100%' },
  headerCell: {
    overflow: 'hidden',
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    flex: '0 0 auto',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(16, 22, 26, 0.15)',
    borderLeft: '1px solid rgba(16, 22, 26, 0.06)',
    backgroundColor: '#ffffff',
    fontWeight: 400
  },
  bodyCell: {
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    flex: '0 0 auto',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(16, 22, 26, 0.06)',
    borderLeft: '1px solid rgba(16, 22, 26, 0.06)',
    whiteSpace: 'nowrap'
  }
}
interface TableProps {
  columns: ReactTableColumn<any>[]
  data: any[]
  resizable?: boolean
  sortable?: boolean
  SubComponent?: JSX.Element | string | ((row: Row<any>) => any)
  onExpandedChange?: (e) => void
  expanded?: { [key: number]: boolean }
}
interface Props {
  tableProps: TableProps
  pageSize: number
  dataLabel: string
  setExpanded?: (expandedObj: { [key: number]: any }) => void
}
const Table = ({
  columns,
  data,
  SubComponent,
  expanded
}: {
  columns: ReactTableColumn<any>[]
  data: any[]
  SubComponent?: JSX.Element | string | ((row: Row<any>) => any)
  expanded?: { [key: number]: boolean }
}) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<any>(
    {
      columns,
      data
    },
    useBlockLayout,
    useResizeColumns
  )
  return (
    <div style={styles.tableScroller}>
      <div {...getTableProps()} className="bp3-html-table bp3-html-table-striped" style={styles.table}>
        <div>
          {headerGroups.map(headerGroup => (
            <div {...headerGroup.getHeaderGroupProps()} style={styles.row}>
              {headerGroup.headers.map(column => (
                <div
                  {...column.getHeaderProps()}
                  style={{
                    ...styles.headerCell,
                    ...column.getHeaderProps().style,
                    borderLeft: column.index === 0 ? 'none' : styles.headerCell.borderLeft
                  }}
                >
                  {column.render('Header')}
                  {(column as any).canResize ? (
                    <div
                      {...(column as any).getResizerProps()}
                      onClick={e => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        height: '100%',
                        width: 8,
                        cursor: 'col-resize',
                        userSelect: 'none',
                        touchAction: 'none',
                        background: (column as any).isResizing ? '#2196f3' : 'transparent',
                        zIndex: 1
                      }}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row)
            return (
              <React.Fragment key={row.id}>
                <div
                  {...row.getRowProps()}
                  style={{
                    ...styles.row,
                    backgroundColor: rowIndex % 2 === 0 ? 'rgba(167, 182, 194, 0.15)' : '#ffffff'
                  }}
                >
                  {row.cells.map(cell => (
                    <div
                      {...cell.getCellProps()}
                      style={{
                        ...styles.bodyCell,
                        ...cell.getCellProps().style,
                        borderLeft: cell.column.index === 0 ? 'none' : styles.bodyCell.borderLeft
                      }}
                    >
                      {cell.render('Cell')}
                    </div>
                  ))}
                </div>
                {SubComponent && expanded?.[row.index] && (
                  <div>
                    <div style={{ width: '100%' }}>
                      {typeof SubComponent === 'function' ? SubComponent(row) : SubComponent}
                    </div>
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}
export const DrawerTable: React.FC<Props> = ({ tableProps, pageSize, dataLabel, setExpanded }) => {
  const [page, setPage] = React.useState(0)
  const currentPageStart = page * pageSize + 1
  const currentPageEnd = Math.min((page + 1) * pageSize, tableProps.data.length)
  const maxPages = React.useMemo(() => {
    return Math.ceil((tableProps.data.length * 1.0) / pageSize) || 0
  }, [tableProps.data.length, pageSize])
  const paginatedData = React.useMemo(() => {
    const start = page * pageSize
    return tableProps.data.slice(start, start + pageSize)
  }, [tableProps.data, page, pageSize])
  return (
    <>
      <Table
        columns={tableProps.columns}
        data={paginatedData}
        SubComponent={tableProps.SubComponent}
        expanded={tableProps.expanded}
      />
      <Divider />
      <ButtonGroup style={styles.container}>
        <Button
          onClick={() => {
            setPage(page - 1)
            if (typeof setExpanded === 'function') setExpanded({})
          }}
          disabled={page === 0}
          style={styles.button}
        >
          Previous
        </Button>
        <div
          style={styles.info}
        >{`${currentPageStart} - ${currentPageEnd} of ${tableProps.data.length} ${dataLabel}`}</div>
        <Button
          onClick={() => {
            setPage(page + 1)
            if (typeof setExpanded === 'function') setExpanded({})
          }}
          disabled={page >= maxPages - 1}
          style={styles.button}
        >
          Next
        </Button>
      </ButtonGroup>
    </>
  )
}
